import React, { useState, useContext, useEffect, useReducer } from "react";
import { Col, Container, Dropdown, Form, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';

import paymentOptions from '../../mockdata/paymentOptions.json';
import { PBLResponseContext, UUIDContext } from "../../UUIDContexted.tsx";
import PaymentNotSupported from "../PaymentNotSupported.tsx";
import CONSTANTS from "../../utils/constants.js";
import { submitWeroPaymentRequest } from "../../api/apiService.ts";
import { FormErrorAction, PaypalFormErrorState } from "types/validationError.types.ts";
import { PaymentProcessingLoader } from "../../components/PaymentProcessingLoader.tsx";
import { validatepaypalForm } from "../../validationFunctions/inputValidations.tsx";
import PaylaDcs from '../../components/PaylaDsc.tsx';

import "./weroPage.css";

const FormFieldError: React.FC<{
    id: string;
    value: string;
    testId: string;
}> = ({ id, value, testId }) =>
        value !== "typing" &&
        value !== "" && (
            <span
                id={id}
                data-testid={testId}
                className="error_message"
            >
                <FormattedMessage id={value} />
            </span>
        );

const FormFieldGroup: React.FC<{
    id: string;
    label: string;
    testId?: string;
    required?: boolean;
    children: React.ReactNode;
}> = ({ id, label, testId, required = true, children }) => (
    <Form.Group controlId={id}>
        <Form.Label data-testid={testId}>
            {label} {required ? (<span style={{ color: '#F50057' }}>*</span>) : ""}
        </Form.Label>
        {children}
    </Form.Group>
);

const CustomDropdown: React.FC<{
    id: string;
    disable: boolean;
    value: string;
    options: { key: string; value: string }[];
    testId?: string;
    onSelect: (value: string) => void;
}> = ({ id, disable = false, value, options, testId, onSelect }) => (
    <Dropdown onSelect={(eventKey) => eventKey && onSelect(eventKey)} className="w-100 volte-medium">
        <Dropdown.Toggle disabled={disable}
            className={`w-100 d-flex justify-content-between align-items-center ${disable ? "disabled-dropdown" : ""}`}
            id={id}
            style={{
                backgroundColor: "white",
                borderColor: "#b1b6ba",
            }}
            data-testid={testId}
        >
            {value}
        </Dropdown.Toggle>
        <Dropdown.Menu className="w-100 rounded-3 form-dropdown">
            {options.map((option) => (
                <Dropdown.Item
                    key={option.key}
                    eventKey={option.key}
                    data-testid={`${testId}-${option.key.toLowerCase()}`}
                >
                    {option.value}
                </Dropdown.Item>
            ))}
        </Dropdown.Menu>
    </Dropdown>
);

const formErrorReducer = (
    errorState: PaypalFormErrorState,
    action: FormErrorAction
): PaypalFormErrorState => {
    if (action.type === "SET_NOT_AGREED_ERROR") {
        return {
            ...errorState,
            notAgreedError: action.value,
        };
    }
    else {
        return errorState;
    }
};

const WeroPage: React.FC = () => {
    const intl = useIntl();
    const PBLData = useContext(UUIDContext);
    const { setPblResponseData } = useContext(PBLResponseContext);
    const { uuid } = useParams<{ uuid: string }>();

    const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
    const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
    const [submissionAttempted, setSubmissionAttempted] = useState(false);
    const [isCountryCodeRequired, setIsCountryCodeRequired] = useState(false);
    const [snippetToken, setSnippetToken] = useState("");

    // Fires when back/forward button is clicked
    window.addEventListener('popstate', (event) => {
        console.log('Navigation intercepted', event.state);
        setShowPaymentProcessing(false)
        // Can react to or potentially block navigation
    });


    const [formData, setFormData] = useState({
        paymentMethod: "900",
        linkId: PBLData?.linkId,
        agreed: false,
        selectedCountry: "Germany"
    });

    const initialErrorState: PaypalFormErrorState = {
        notAgreedError: ""
    };

    const handleSubmit = async () => {
        validatepaypalForm(
            formData,
            errorDispatch
        );
        setSubmissionAttempted(true);
        setShowPaymentProcessing(false);
    }

    const [formErrorState, errorDispatch] = useReducer(
        formErrorReducer,
        initialErrorState
    );

    //this function recurses through formErrorState and search for non-empty strings (errors) and returns true if errors not present
    const containsError = (errors: PaypalFormErrorState): boolean => {
        const check = (obj: any): boolean => {
            for (const key in obj) {
                if (typeof obj[key] === "object") {
                    if (check(obj[key])) {
                        return true;
                    }
                } else if (typeof obj[key] === "string" && obj[key] !== "") {
                    return true;
                }
            }
            return false;
        };
        return check(errors);
    };

    const checkRequiredParameters = () => {
        const requiredParameters = PBLData?.requiredParameters;
        let productId;
        if (requiredParameters) {
            requiredParameters.forEach((item) => {
                productId = Object.keys(item).find(key => {
                    return key === "900";
                });
            });
        }
        setIsCountryCodeRequired(productId === "900");
    }

    //generate snippetToken only once
    useEffect(() => {
        const partnerMerchantId = PBLData ? PBLData.bnplId : '';
        setSnippetToken(`${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`);
        checkRequiredParameters();
        setPblResponseData(null);
    }, []);

    useEffect(() => {
        if (PBLData?.paymentMethods && PBLData?.paymentMethods.length > 0) {
            let paypalPaymentMethodsID = paymentOptions.find(option => option.id === "wero")?.paymentMethodsID;
            if (paypalPaymentMethodsID) {
                let isPaymentMethodAvailable = PBLData.paymentMethods.includes(paypalPaymentMethodsID);
                setIsPaymentMethodAvailable(isPaymentMethodAvailable)
            }
        }
    }, [PBLData]);

    useEffect(() => {
        const makePostRequest = async () => {
            if (submissionAttempted) {
                const hasError = containsError(formErrorState);

                if (!hasError) {
                    setShowPaymentProcessing(true);
                    setPblResponseData(null);
                    // code for the post request
                    try {
                        const response = await submitWeroPaymentRequest(formData, snippetToken, isCountryCodeRequired);
                        // Check if actionType is REDIRECT
                        if (response.createPaymentResponse?.merchantAction?.actionType === 'REDIRECT') {
                            const redirectURL = response.createPaymentResponse.merchantAction.redirectData?.redirectURL;
                            if (redirectURL && /^https?:\/\//.test(redirectURL)) {
                                // Redirect to Wero
                                window.location.href = redirectURL;
                            } else {
                                setPblResponseData({ error: 'CUSTOM_ERROR_WERO', linkId: uuid });
                                setShowPaymentProcessing(false);
                            }
                        }
                    } catch (error: any) {
                        setPblResponseData({ error: error, linkId: uuid });
                        setShowPaymentProcessing(false);
                    }
                }
            }
        }
        makePostRequest();

        return () => {
            setSubmissionAttempted(false);
        }
    }, [submissionAttempted]);

    return (
        <>
            {isPaymentMethodAvailable ?
                <Container fluid className="wero_page d-flex flex-column g-0" data-testid="wero_payment_section">
                    <Row className="flex-grow-1">
                        <Col xs={12} md={12} className="mb-24">
                            <FormFieldGroup
                                id="country"
                                label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COUNTRY, defaultMessage: "Country" })}
                                testId="pm-country-label"
                            >
                                <CustomDropdown
                                    id="country"
                                    disable={false}
                                    value={`${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY[formData.selectedCountry.toUpperCase() as keyof typeof CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY], defaultMessage: formData.selectedCountry })}`}
                                    options={[
                                        {
                                            key: "Germany",
                                            value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.GERMANY, defaultMessage: "Germany" })}`,
                                        },
                                        {
                                            key: "France",
                                            value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.FRANCE, defaultMessage: "France" })}`,
                                        },
                                        {
                                            key: "Belgium",
                                            value: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.BELGIUM, defaultMessage: "Belgium" })}`
                                        }
                                    ]}
                                    testId="pm-country-input"
                                    onSelect={(value) => {
                                        setFormData((prevState) => ({
                                            ...prevState,
                                            selectedCountry: value
                                        }));
                                    }}
                                />
                            </FormFieldGroup>
                        </Col>
                    </Row>

                    <Row className="d-flex justify-content-center align-items-end mb-24 mt-2">
                        <h5 data-testid="pm-label-terms-payment" className="section-title">
                            {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE, defaultMessage: "Terms of Payment" })}
                        </h5>
                        <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
                            <Form.Check.Input
                                type="checkbox"
                                className="terms-checkbox-input m-0"
                                checked={formData.agreed}
                                onChange={(e) => setFormData((prevState) => ({
                                    ...prevState,
                                    agreed: e.target.checked,
                                }))}
                                data-testid="pm-terms-checkbox-input"
                            />
                            <label className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
                                <FormattedMessage
                                    id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
                                    defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
                                />
                            </label>
                        </div>
                        {formData.agreed ? (
                            (formErrorState.notAgreedError = "")
                        ) : (
                            <FormFieldError
                                id="not-agree"
                                value={formErrorState.notAgreedError}
                                testId="pn-not-agree"
                            />
                        )}
                        <PaylaDcs snippetToken={snippetToken} agreed={formData.agreed} />
                        <div className="term_policy_text mt-3"
                            dangerouslySetInnerHTML={{
                                __html: intl.formatMessage({
                                    id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_DESCRIPTION_WERO,
                                    defaultMessage: CONSTANTS.CHECKBOX_DESCRIPTION_WERO,
                                }),
                            }}
                        ></div>
                    </Row>

                    <Row className="d-flex align-items-center">
                        {showPaymentProcessing ? (
                            <Col>
                                <PaymentProcessingLoader />
                            </Col>
                        ) : (
                            <Col className="text-end">
                                <button
                                    className="btn btn-primary text-white px-4 rounded-4 volte-medium wero-submit-btn"
                                    onClick={handleSubmit}
                                    data-testid="pm-submit-btn"
                                >
                                    <FormattedMessage
                                        id={CONSTANTS.PAYMENT_FORM.BUTTON.SUBMIT_PAYMENT}
                                        defaultMessage="Complete payment"
                                    />
                                </button>
                            </Col>
                        )}
                    </Row>
                </Container>
                : <PaymentNotSupported />
            }
        </>
    );
};

export default React.memo(WeroPage);
